import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { Box, CssBaseline, ThemeProvider } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { IdleTimerProvider } from 'react-idle-timer'
import './App.css'
const Home = React.lazy(() => import('./modules/home/Home'))
import {
  AccountNotVerifiedTemplate,
  AccountVerifiedTemplate
} from './modules/login/template/AccountVerificationTemplates'
import { PrintContext, PrintContextType, usePrint } from './shared/hooks/usePrint'
import rcmBlueTheme from './shared/themes/RcmBlue'
import { AppPublicRoutes } from './App.Public.Routes'
import { useAppDispatch, useAppSelector } from './store/hooks'
import { getUAuthAuthenticated, getSimulationReq, SetUAuthLoginData } from './store/slice'
import { Logger } from './system/appinsights/Logger'
import { AppUnverifiedRoutes } from './App.UnVerified.Routes'
import {
  CustomMfaAuthenticatedTemplate,
  CustomMfaUnAuthenticatedTemplate
} from './modules/login/template/CustomMfaVerificationTemplates'
import AppMfaUnVerifiedRoutes from './App.Mfa.UnVerified.Routes'
import useAppSignout from './shared/hooks/useAppSignout'
import { UAuthTokenKey } from './shared/Constants'
import { getSessionStorage } from './shared/utilities/Session'

const App: React.FC = () => {
  const simulationReq = useAppSelector(getSimulationReq)
  const isUAuthAuthenticated = useAppSelector(getUAuthAuthenticated)
  const dispatch = useAppDispatch()
  const timeout = simulationReq ? 60 * 1000 * 60 : 60 * 1000 * 20
  const { handleSignout } = useAppSignout()
  const { handlePrint } = usePrint()

  useEffect(() => {
    const storedToken = getSessionStorage(UAuthTokenKey)
    if (storedToken) {
      dispatch(SetUAuthLoginData(storedToken))
    }
  }, [dispatch])
  const onIdle = () => {
    Logger.LogInfo({ message: 'Application went idle, logout redirect initiated' })
    handleSignout({ postLogoutRedirectUri: '/timeout' })
  }
  const printContextValue: PrintContextType = useMemo(() => ({ print: handlePrint }), [handlePrint])
  if (isUAuthAuthenticated) {
    return (
      <ThemeProvider theme={rcmBlueTheme}>
        <IdleTimerProvider timeout={timeout} onIdle={onIdle}>
          <CssBaseline />
          <Box component="div">
            <PrintContext.Provider value={printContextValue}>
              <React.Suspense>
                <Home />
              </React.Suspense>
            </PrintContext.Provider>
          </Box>
        </IdleTimerProvider>
      </ThemeProvider>
    )
  }
  return (
    <ThemeProvider theme={rcmBlueTheme}>
      <AuthenticatedTemplate>
        <CustomMfaAuthenticatedTemplate>
          <IdleTimerProvider timeout={timeout} onIdle={onIdle}>
            <CssBaseline />
            <AccountVerifiedTemplate>
              <Box component="div">
                <PrintContext.Provider value={printContextValue}>
                  <React.Suspense>
                    <Home />
                  </React.Suspense>
                </PrintContext.Provider>
              </Box>
            </AccountVerifiedTemplate>
            <AccountNotVerifiedTemplate>
              <AppUnverifiedRoutes />
            </AccountNotVerifiedTemplate>
          </IdleTimerProvider>
        </CustomMfaAuthenticatedTemplate>
        <CustomMfaUnAuthenticatedTemplate>
          <AppMfaUnVerifiedRoutes />
        </CustomMfaUnAuthenticatedTemplate>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <AppPublicRoutes />
      </UnauthenticatedTemplate>
    </ThemeProvider>
  )
}

export default App
